import { useQuery } from '@tanstack/react-query'

import { toChatMessage, type MessageWire } from '@/features/chat/api/useConversation'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { ChatMessage } from '@/types/domain'

/* The source quote's conversation, as its case shows it (S-1, owner decision
 * O3, collab chat_dto.go QuoteArchiveDTO).
 *
 * GET /api/cases/:id/quote-conversation answers whoever may read the CASE's
 * chat — the team, the owner, `collab.chat.read.all` — which is deliberately
 * wider than who may open the quote: Operations staffed on the case read the
 * pricing conversation that led to it. `quoteReachable` says whether this
 * reader may also open the quote itself, and is the only thing a link to it
 * may be drawn from.
 *
 * The thread is FROZEN — a converted quote's conversation refuses posts — so
 * this is one read, never polled, and there is no `after`. A 404 means the case
 * has no source quote or this reader may not read its chat; the block treats
 * both as nothing to show.
 */

interface QuoteArchiveWire {
  quoteId: string
  quoteNumber: string
  quoteReachable: boolean
  items: MessageWire[]
  hasMore: boolean
}

export interface QuoteArchive {
  quoteId: string
  quoteNumber: string
  /** Whether this reader may open the quote — the link's only source. */
  quoteReachable: boolean
  /** Oldest first, as the chat serves a page. */
  messages: ChatMessage[]
  /** More messages exist before the first one held. */
  hasMore: boolean
}

/** One read carries the whole archive of any ordinary quote: the server's
 *  MaxPageSize (collab chat.go). A longer thread shows its latest part and
 *  says so, rather than growing a pager for a conversation nobody adds to. */
export const QUOTE_ARCHIVE_LIMIT = 200

export function useQuoteArchive(caseId: string, enabled: boolean) {
  return useQuery<QuoteArchive>({
    queryKey: queryKeys.cases.quoteConversation(caseId),
    enabled,
    queryFn: async () => {
      if (USE_MOCKS) return NO_ARCHIVE
      const wire = await api.get<QuoteArchiveWire>(`/cases/${caseId}/quote-conversation`, {
        params: { limit: QUOTE_ARCHIVE_LIMIT },
      })
      return {
        quoteId: wire.quoteId,
        quoteNumber: wire.quoteNumber,
        quoteReachable: wire.quoteReachable,
        messages: wire.items.map(toChatMessage),
        hasMore: wire.hasMore,
      }
    },
    /* Frozen on the server, so a re-read can only bring a different reach
       (`quoteReachable`) — which a page reload is soon enough for. */
    staleTime: 5 * 60 * 1000,
  })
}

/* Offline the block stays hidden: the fixtures pair no quote thread with the
   case it became, and borrowing a transcript that belongs to another record
   would show a conversation that never happened on this one. */
const NO_ARCHIVE: QuoteArchive = {
  quoteId: '',
  quoteNumber: '',
  quoteReachable: false,
  messages: [],
  hasMore: false,
}
